/**
 * ───────────────────────────────────────────────────────────────────────────
 * PWA INSTALL — SINGLE SOURCE OF TRUTH
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Owns every piece of install state so no component has to guess:
 *   - capturing and storing the Chromium `beforeinstallprompt` event
 *   - platform detection (iOS has no programmatic prompt — it needs the
 *     Share → Add to Home Screen instructions instead)
 *   - the trigger rules (2nd visit for anonymous, key actions for members)
 *   - the anti-spam rules (30 days after a dismissal, never after install)
 *   - the analytics events
 *
 * HONEST LIMITS, stated up front because they shape the UI:
 *   - `beforeinstallprompt` is Chromium-only. Safari and Firefox never fire
 *     it, so on those browsers we can only show instructions, never a button
 *     that installs. We therefore only show the iOS instruction variant on
 *     iOS, and stay completely silent on desktop Firefox/Safari rather than
 *     showing an "Add App" button that would do nothing.
 *   - We cannot detect with certainty that an iOS user completed the manual
 *     Add to Home Screen steps. `pwa_installed` with platform `ios` is
 *     recorded when the app is later OPENED in standalone mode — that is a
 *     real signal, not an assumption made at the moment we showed the steps.
 */
import { track } from '@/lib/tracking';

const STATE_KEY = 'gg_pwa';
const SESSION_KEY = 'gg_pwa_session';
const DISMISS_DAYS = 30;

/** Where the prompt is allowed to come from. */
export type PwaTrigger =
  | 'second_visit'
  | 'dashboard'
  | 'job_alert_saved'
  | 'lead_purchased'
  | 'project_posted';

export type PwaPlatform = 'android' | 'ios' | 'desktop';

interface PwaState {
  /** Distinct browsing sessions counted for this browser. */
  visits: number;
  /** ISO timestamp of the last "Not Now". */
  dismissedAt?: string;
  /** Set once the app is known to be installed. */
  installed?: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

/* ── storage helpers (never throw: private mode / blocked storage) ───────── */

const read = (): PwaState => {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? (JSON.parse(raw) as PwaState) : {};
    return { visits: Number(parsed.visits) || 0, dismissedAt: parsed.dismissedAt, installed: !!parsed.installed };
  } catch {
    return { visits: 0 };
  }
};

const write = (next: PwaState) => {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(next));
  } catch {
    /* storage blocked — the prompt simply behaves like a first visit */
  }
};

/* ── platform detection ─────────────────────────────────────────────────── */

export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const displayMode =
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches);
  // iOS Safari exposes a non-standard flag instead of display-mode.
  const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return !!displayMode || iosStandalone;
};

/** True for iPhone / iPad (including iPadOS, which reports as a Mac + touch). */
export const isIos = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadOs = /Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document;
  return /iPad|iPhone|iPod/.test(ua) || iPadOs;
};

/** iOS only offers Add to Home Screen from Safari, not Chrome/Firefox in-app. */
export const isIosSafari = (): boolean => {
  if (!isIos()) return false;
  const ua = navigator.userAgent || '';
  return !/CriOS|FxiOS|EdgiOS|OPiOS|Instagram|FBAN|FBAV|Line\//i.test(ua);
};

export const platform = (): PwaPlatform => {
  if (isIos()) return 'ios';
  if (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')) return 'android';
  return 'desktop';
};

/* ── observability ──────────────────────────────────────────────────────── */

interface PwaLogEntry {
  t: string;
  name: string;
  detail: unknown;
}

interface PwaWindow extends Window {
  __ggPwa?: { event: unknown; log: PwaLogEntry[]; fired: boolean; installed: boolean };
  __ggPwaLog?: (name: string, detail?: unknown) => void;
  __ggPwaAdopt?: (e: Event) => void;
  __ggPwaDebug?: Record<string, unknown>;
}

const w = (): PwaWindow | null => (typeof window === 'undefined' ? null : (window as PwaWindow));

/**
 * Writes to the same ring buffer the inline <head> script uses AND to the
 * console, so a real device can be diagnosed from /pwa-check with no cable.
 */
export const pwaLog = (name: string, detail?: unknown) => {
  const win = w();
  if (!win) return;
  if (typeof win.__ggPwaLog === 'function') {
    win.__ggPwaLog(name, detail);
    return;
  }
  // eslint-disable-next-line no-console
  console.log(`[GIGGONE PWA] ${name}`, detail ?? '');
};

export const pwaLogEntries = (): PwaLogEntry[] => w()?.__ggPwa?.log?.slice() ?? [];

/** True if Chromium fired beforeinstallprompt at any point this page view. */
export const installEventFired = (): boolean => !!w()?.__ggPwa?.fired;

/* ── the captured install event ─────────────────────────────────────────── */

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

/** Subscribe to any change in install availability / pending triggers. */
export const onPwaChange = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const hasInstallEvent = (): boolean => deferred !== null;

/* ── trigger queue ──────────────────────────────────────────────────────── */

let pendingTrigger: PwaTrigger | null = null;

/**
 * Called from the app at the moments worth asking: a dashboard visit, a saved
 * job alert, a purchased lead, a posted project. Marking a trigger does NOT
 * force the modal open — `decidePrompt()` still applies every anti-spam rule.
 */
export const markPwaTrigger = (trigger: PwaTrigger) => {
  pendingTrigger = trigger;
  pwaLog('trigger marked', { trigger });
  notify();
};

export const consumeTrigger = (): PwaTrigger | null => {
  const t = pendingTrigger;
  pendingTrigger = null;
  return t;
};

export const peekTrigger = (): PwaTrigger | null => pendingTrigger;

/* ── lifecycle ──────────────────────────────────────────────────────────── */

export const markInstalled = (source: PwaPlatform) => {
  const s = read();
  write({ ...s, installed: true });
  pendingTrigger = null;
  notify();
  pwaLog('install recorded', { platform: source });
  track('pwa_installed', { platform: source });
};

export const markDismissed = (trigger: PwaTrigger | null) => {
  const s = read();
  write({ ...s, dismissedAt: new Date().toISOString() });
  pendingTrigger = null;
  notify();
  pwaLog('prompt dismissed', { trigger: trigger || 'unknown', suppressedForDays: DISMISS_DAYS });
  track('pwa_install_dismissed', { platform: platform(), trigger: trigger || 'unknown' });
};

export const markShown = (trigger: PwaTrigger | null) => {
  pwaLog('prompt shown', { trigger: trigger || 'unknown', platform: platform() });
  track('pwa_prompt_shown', { platform: platform(), trigger: trigger || 'unknown' });
};

/** Fires the real Chromium install dialog. Returns the user's choice. */
export const promptInstall = async (
  trigger: PwaTrigger | null
): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
  if (!deferred) {
    pwaLog('prompt() skipped — no captured beforeinstallprompt event');
    return 'unavailable';
  }
  const evt = deferred;
  deferred = null; // a beforeinstallprompt event can only be used once
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === 'accepted') {
      pwaLog('prompt accepted', { trigger: trigger || 'unknown' });
      track('pwa_install_accepted', { platform: platform(), trigger: trigger || 'unknown' });
      // `appinstalled` records the completed install; this only records intent.
      return 'accepted';
    }
    markDismissed(trigger);
    return 'dismissed';
  } catch (err) {
    pwaLog('prompt() threw', { message: (err as Error)?.message });
    return 'unavailable';
  } finally {
    notify();
  }
};

/* ── the decision ───────────────────────────────────────────────────────── */

const dismissedRecently = (s: PwaState): boolean => {
  if (!s.dismissedAt) return false;
  const when = Date.parse(s.dismissedAt);
  if (Number.isNaN(when)) return false;
  return Date.now() - when < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

export interface PromptDecision {
  show: boolean;
  /** 'native' = real install button available. 'ios' = manual instructions. */
  mode: 'native' | 'ios';
  trigger: PwaTrigger | null;
  reason?: string;
}

/** Bypasses the visit/dismissal rules for on-device testing only. */
let forced = false;
export const forcePrompt = () => {
  forced = true;
  pwaLog('force prompt requested (diagnostics)');
  notify();
};

export const decidePrompt = (): PromptDecision => {
  const none: PromptDecision = { show: false, mode: 'native', trigger: null };
  if (typeof window === 'undefined') return none;

  const s = read();
  if (!forced) {
    if (s.installed) return { ...none, reason: 'installed' };
    if (isStandalone()) return { ...none, reason: 'standalone' };
    if (dismissedRecently(s)) return { ...none, reason: 'dismissed' };
  }

  const trigger = pendingTrigger || (s.visits >= 2 ? 'second_visit' : forced ? 'second_visit' : null);
  if (!trigger) return { ...none, reason: 'no-trigger' };

  // iOS cannot be installed programmatically — show the Share sheet steps.
  if (isIos()) {
    if (!isIosSafari() && !forced) return { ...none, reason: 'ios-non-safari' };
    return { show: true, mode: 'ios', trigger };
  }

  // Everywhere else we only ask when the browser actually offered to install,
  // so the "Add App" button is never a dead button.
  if (!deferred) return { ...none, reason: 'no-install-event' };
  return { show: true, mode: 'native', trigger };
};

/**
 * Plain-English explanation of why the modal is or is not on screen. Rendered
 * verbatim on /pwa-check so a tester never has to guess.
 */
export const reasonText = (reason?: string): string => {
  switch (reason) {
    case 'installed':
      return 'Suppressed: this browser has already recorded an install (localStorage gg_pwa.installed = true). Use Reset below to clear it.';
    case 'standalone':
      return 'Suppressed: the page is already running in standalone/installed mode.';
    case 'dismissed':
      return `Suppressed: "Not Now" was tapped within the last ${DISMISS_DAYS} days. Use Reset below to clear it.`;
    case 'no-trigger':
      return 'Waiting: no trigger yet. Anonymous visitors need a 2nd browsing session; members trigger on dashboard, saved alert, lead purchase or posted project.';
    case 'ios-non-safari':
      return 'Suppressed: iOS, but not Safari. Only Safari can Add to Home Screen on iOS.';
    case 'no-install-event':
      return 'Blocked: Chromium never fired beforeinstallprompt, so there is nothing to install with. See the checks above for the cause.';
    default:
      return 'Eligible: the modal should be visible.';
  }
};

/* ── state reset (diagnostics) ──────────────────────────────────────────── */

export const resetPwaState = () => {
  try {
    localStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  forced = false;
  pendingTrigger = null;
  pwaLog('state reset (diagnostics)');
  notify();
};

export const readPwaState = (): PwaState => read();
export const dismissWindowDays = DISMISS_DAYS;

/* ── init ───────────────────────────────────────────────────────────────── */

let started = false;

/**
 * Installs the listeners and counts the visit. Called once from main.tsx,
 * BEFORE React renders. The authoritative listener is the inline one in
 * index.html (it runs during HTML parse, so it cannot miss the event); this
 * adopts whatever that listener captured and takes over from there.
 */
export const initPwa = () => {
  if (started || typeof window === 'undefined') return;
  started = true;

  const win = w();

  // 1. Adopt an event the inline <head> listener already caught.
  const early = win?.__ggPwa?.event;
  if (early) {
    deferred = early as BeforeInstallPromptEvent;
    pwaLog('adopted beforeinstallprompt captured before bundle load');
  }

  // 2. Let the inline listener hand future events straight to us.
  if (win) {
    win.__ggPwaAdopt = (e: Event) => {
      deferred = e as BeforeInstallPromptEvent;
      notify();
    };
  }

  // 3. Own listener as well — harmless if the inline one already ran.
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault(); // stop Chrome's mini-infobar; we show our own modal
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    markInstalled(platform());
  });

  // Count one visit per browsing session (a reload is not a new visit).
  try {
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, '1');
      const s = read();
      write({ ...s, visits: s.visits + 1 });
    }
  } catch {
    /* ignore */
  }

  // Someone who opens the installed app should never be asked again.
  if (isStandalone()) {
    const s = read();
    if (!s.installed) markInstalled(platform());
  }

  const s = read();
  pwaLog('init', {
    platform: platform(),
    visits: s.visits,
    installedFlag: !!s.installed,
    dismissedAt: s.dismissedAt || null,
    standalone: isStandalone(),
    haveInstallEvent: !!deferred,
    inIframe: window.top !== window.self,
    secureContext: window.isSecureContext,
    swSupported: 'serviceWorker' in navigator,
  });

  // Console-accessible debug handle for a real device / remote debugging.
  if (win) {
    win.__ggPwaDebug = {
      status: () => ({ ...read(), haveInstallEvent: !!deferred, decision: decidePrompt() }),
      force: forcePrompt,
      reset: resetPwaState,
      log: pwaLogEntries,
    };
  }
};

export const visitCount = (): number => read().visits;
